import InputAdornment from '@mui/material/InputAdornment';

import { Box } from '../../Box';
import { Icon } from '../../Icon';

/**
 * Свойства компонента {@link ShowPasswordButton}
 */
interface ShowPasswordButtonProps {
  /**
   * Указывает, является ли пароль в данный момент видимым.
   */
  isShow: boolean;

  /**
   * Если true, кнопка будет отключена.
   */
  disabled: boolean;

  /**
   * Если true, кнопка будет отмечена цветом ошибки.
   */
  error: boolean;

  /**
   * Функция обратного вызова для обработки событий клика.
   */
  onClick: () => void;
}

/**
 * Определяет цвет иконки в зависимости от состояний disabled и error.
 *
 * @param disabled - Если true, кнопка отключена.
 * @param error - Если true, есть ошибка в поле ввода.
 * @returns  - Цвет иконки.
 */
const getIconColor = (disabled: boolean, error: boolean) => {
  if (disabled) return 'stroke.secondary';
  if (error) return 'accent.text.error';
  return 'original.brand.primary';
};

/**
 * ShowPasswordButton - это компонент, который переключает видимость поля ввода пароля.
 * Он отображает либо иконку видимости, либо иконку невидимости в зависимости от пропса `isShow`.
 *
 * @internal
 * @param isShow - Указывает, является ли пароль в данный момент видимым.
 * @param disabled - Если true, кнопка будет отключена.
 * @param error - Если true, кнопка будет отмечена цветом ошибки.
 * @param onClick - Функция обратного вызова для обработки событий клика.
 * @returns  - JSX компонента.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link ShowPasswordButtonProps}
 */
export const ShowPasswordButton = ({
  isShow,
  disabled,
  error,
  onClick,
}: ShowPasswordButtonProps) => (
  <InputAdornment position="end">
    <Box height={24} onClick={onClick} testId="text-input-show-password">
      {isShow ? (
        <Icon name="view" size="24" color={getIconColor(disabled, error)} />
      ) : (
        <Icon name="hide" size="24" color={getIconColor(disabled, error)} />
      )}
    </Box>
  </InputAdornment>
);
